import { useEffect, useState } from "react";
import api from "../../api/client";
import { MAX_NAP_PHOTO_BYTES } from "../common/NapFieldsGroup";

/**
 * AddNapCommentModal — capture a free-text NAP observation, with an optional
 * photo and a "Full NAP" flag. Posts to POST /nap-locator/{recordId}/comments.
 *
 * Props:
 *   recordId          - NAP Locator record id (required)
 *   napCode           - display only
 *   initialFullNap    - current manual_full_nap state (pre-fills the checkbox)
 *   maxAvailablePorts - max value for the Available Ports field (total ports − completed jobs)
 *   onClose           - () => void
 *   onSaved           - (savedComment) => void
 */
export default function AddNapCommentModal({ recordId, napCode, initialFullNap = false, maxAvailablePorts = null, onClose, onSaved }) {
  const [comment, setComment] = useState("");
  const [fullNap, setFullNap] = useState(!!initialFullNap);
  const [availablePorts, setAvailablePorts] = useState(initialFullNap ? "0" : "");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Full NAP and Available Ports are mutually linked:
  //   Full NAP checked  ⟺  available ports = 0
  //   any available ports > 0  ⟺  not Full NAP
  const toggleFullNap = (checked) => {
    setFullNap(checked);
    setAvailablePorts(checked ? "0" : "");
  };

  const changeAvailablePorts = (raw) => {
    if (raw === "") { setAvailablePorts(""); setFullNap(false); return; }
    let n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) n = 0;
    if (maxAvailablePorts != null && n > maxAvailablePorts) n = maxAvailablePorts;
    setAvailablePorts(String(n));
    setFullNap(n === 0);
  };

  useEffect(() => {
    if (!photo) { setPhotoPreview(null); return; }
    const url = URL.createObjectURL(photo);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoError("");
    if (!file.type.startsWith("image/")) { setPhotoError("Please select an image file."); return; }
    if (file.size > MAX_NAP_PHOTO_BYTES) { setPhotoError("Photo must be 5MB or smaller."); return; }
    setPhoto(file);
  };

  // Photo required when marking Full NAP or when reporting available ports.
  const portsProvided = availablePorts !== "";
  const photoRequired = fullNap || portsProvided;
  const canSave = comment.trim() && (!photoRequired || !!photo) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("comment", comment.trim());
      fd.append("is_full_nap", fullNap ? "true" : "false");
      if (portsProvided) fd.append("available_ports", String(parseInt(availablePorts, 10)));
      if (photo) fd.append("file", photo);
      const { data } = await api.post(`/nap-locator/${recordId}/comments`, fd);
      onSaved?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save comment.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b">
          <h3 className="text-base font-bold text-gray-800">Add NAP Comment</h3>
          {napCode && <p className="text-xs text-gray-500 mt-0.5">NAP: {napCode}</p>}
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-3 py-2">{error}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Comment <span className="text-red-500">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Describe the observation for this NAP…"
              className="w-full border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Full NAP toggle */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={fullNap}
              onChange={(e) => toggleFullNap(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium">Mark this NAP as Full NAP</span>
              <span className="block text-xs text-gray-500">
                Flags the NAP as full (red on the map) even if the system still shows vacant ports. Sets available ports to 0. A photo is required.
              </span>
            </span>
          </label>

          {/* Available ports */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Available Ports <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="number"
              min={0}
              max={maxAvailablePorts ?? undefined}
              value={availablePorts}
              onChange={(e) => changeAvailablePorts(e.target.value)}
              placeholder={maxAvailablePorts != null ? `0 - ${maxAvailablePorts}` : "Actual number of available ports"}
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              If filled, a photo is required. Entering 0 marks the NAP as Full.
              {maxAvailablePorts != null && ` Max ${maxAvailablePorts} (total ports − completed jobs).`}
            </p>
          </div>

          {/* Photo upload */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Photo {photoRequired ? <span className="text-red-500">*</span> : <span className="text-gray-400">(optional)</span>}
            </label>
            {photo ? (
              <div className="flex items-center gap-3">
                {photoPreview && (
                  <img src={photoPreview} alt="NAP" className="h-16 w-16 object-cover rounded border flex-shrink-0" />
                )}
                <span className="text-xs text-gray-600 truncate flex-1">{photo.name}</span>
                <button
                  type="button"
                  onClick={() => { setPhoto(null); setPhotoError(""); }}
                  className="text-xs text-red-600 hover:underline flex-shrink-0"
                >
                  Remove
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoPick}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            )}
            {photoError ? (
              <p className="text-xs text-red-600 mt-1">{photoError}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">Image only, max 5MB.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-5 py-2 text-sm text-white rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Comment"}
          </button>
        </div>
      </div>
    </div>
  );
}
